import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, X, Eye, EyeOff, Type, FileText, Image, Search, Save, Upload } from "lucide-react";
import apiClient, { getArticles, createArticle, updateArticle, deleteArticle } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ArticlesCMS = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("contenu");
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef(null);

  const [formData, setFormData] = useState({
    titre: "",
    contenu: "",
    extrait: "",
    image: "",
    galerie: [],
    meta_title: "",
    meta_description: "",
    publie: false,
  });
  const [newGalerieUrl, setNewGalerieUrl] = useState("");
  const [newLocalUrl, setNewLocalUrl] = useState("");

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append("file", file);
      const res = await apiClient.post("/upload", formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND_URL}${res.data.url}`;
      // Add directly to the local images library
      saveLocalImages([url, ...localImages]);
      setFormData(f => ({ ...f, image: url }));
      toast.success("Image uploadée et ajoutée à la bibliothèque !");
    } catch {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const DEFAULT_LOCAL_IMAGES = [
    "/images/photo-1669300568006.jpg",
    "/images/photo-1659027793165.jpg",
    "/images/photo-1760372060494.jpg",
    "/images/pexels-6782578.jpg",
    "/images/photo-1773847400278.jpg",
    "/images/photo-1757924461488.jpg",
    "/images/photo-1600585154340.jpg",
    "/images/photo-1554224155.jpg",
    "/images/photo-1773847408152.jpg",
    "/images/directrice.jpg",
  ];

  const [localImages, setLocalImages] = useState(() => {
    try {
      const saved = sessionStorage.getItem("hh_local_images");
      return saved ? JSON.parse(saved) : DEFAULT_LOCAL_IMAGES;
    } catch { return DEFAULT_LOCAL_IMAGES; }
  });

  const saveLocalImages = (imgs) => {
    setLocalImages(imgs);
    try { sessionStorage.setItem("hh_local_images", JSON.stringify(imgs)); } catch {}
  };

  const addLocalImage = () => {
    const url = newLocalUrl.trim();
    if (!url || localImages.includes(url)) return;
    saveLocalImages([...localImages, url]);
    setNewLocalUrl("");
  };

  const removeLocalImage = (url) => {
    saveLocalImages(localImages.filter(i => i !== url));
  };

  const addToGalerie = (url) => {
    if (!url.trim()) return;
    if (formData.galerie.includes(url.trim())) return;
    setFormData(f => ({ ...f, galerie: [...f.galerie, url.trim()] }));
    setNewGalerieUrl("");
  };

  const removeFromGalerie = (index) => {
    setFormData(f => ({ ...f, galerie: f.galerie.filter((_, i) => i !== index) }));
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [formData.contenu, showModal]);

  const loadData = async () => {
    try {
      const res = await getArticles();
      setArticles(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      if (editing) {
        await updateArticle(editing.id, formData);
        toast.success("Article mis à jour !");
      } else {
        await createArticle(formData);
        toast.success("Article créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (article) => {
    setEditing(article);
    setFormData({
      titre: article.titre,
      contenu: article.contenu,
      extrait: article.extrait,
      image: article.image || "",
      galerie: article.galerie || [],
      meta_title: article.meta_title || "",
      meta_description: article.meta_description || "",
      publie: article.publie,
    });
    setNewGalerieUrl("");
    setActiveTab("contenu");
    setPreview(false);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await deleteArticle(id);
      toast.success("Article supprimé !");
      loadData();
    } catch {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ titre: "", contenu: "", extrait: "", image: "", galerie: [], meta_title: "", meta_description: "", publie: false });
    setNewGalerieUrl("");
    setActiveTab("contenu");
    setPreview(false);
  };

  const isHtml = formData.contenu.trim().startsWith("<");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      {articles.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-4 font-semibold text-[#004333]">Titre</th>
                <th className="text-left p-4 font-semibold text-[#004333] hidden sm:table-cell">Statut</th>
                <th className="text-right p-4 font-semibold text-[#004333]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <p className="font-medium text-[#004333]">{article.titre}</p>
                    {article.extrait && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{article.extrait}</p>}
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${article.publie ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {article.publie ? "Publié" : "Brouillon"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => handleEdit(article)} className="p-2 text-gray-400 hover:text-[#004333] transition-colors"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(article.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-gray-500">
          Aucun article. Créez votre premier article de blog.
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-[#F5F0EB] flex flex-col overflow-hidden">

          {/* Top bar */}
          <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-3 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <FileText className="w-5 h-5 text-[#C9922A] flex-shrink-0" />
              <input
                type="text"
                required
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Titre de l'article..."
                className="text-lg font-semibold text-[#004333] bg-transparent border-none outline-none w-full placeholder-gray-300"
              />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm text-gray-500 hidden sm:inline">{formData.publie ? "Publié" : "Brouillon"}</span>
                <button
                  type="button"
                  onClick={() => setFormData(f => ({ ...f, publie: !f.publie }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${formData.publie ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.publie ? "translate-x-5" : ""}`} />
                </button>
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#004333] text-white text-sm font-semibold rounded-lg hover:bg-[#005544] transition-colors"
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{editing ? "Mettre à jour" : "Créer"}</span>
              </button>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 flex items-center px-6 bg-white border-b border-gray-200">
            {[
              { id: "contenu", icon: Type, label: "Contenu" },
              { id: "extrait", icon: FileText, label: "Extrait" },
              { id: "media", icon: Image, label: "Image" },
              { id: "seo", icon: Search, label: "SEO" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setActiveTab(tab.id); setPreview(false); }}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id ? "border-[#C9922A] text-[#C9922A]" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto px-6 py-6">

              {activeTab === "contenu" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Contenu</label>
                      {isHtml && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">HTML</span>}
                    </div>
                    {isHtml && (
                      <button
                        type="button"
                        onClick={() => setPreview(p => !p)}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#004333] border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
                      >
                        {preview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {preview ? "Éditer" : "Aperçu"}
                      </button>
                    )}
                  </div>

                  {preview && isHtml ? (
                    <div
                      className="min-h-[60vh] bg-white rounded-xl border border-gray-200 p-8 article-html-content"
                      dangerouslySetInnerHTML={{ __html: formData.contenu }}
                    />
                  ) : (
                    <textarea
                      ref={textareaRef}
                      required
                      value={formData.contenu}
                      onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                      placeholder={"Saisissez le contenu...\n\nHTML accepté :\n<h2>Titre</h2>\n<p>Paragraphe</p>"}
                      className="w-full min-h-[60vh] bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-700 font-mono leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 focus:border-[#C9922A]/50 transition-colors"
                    />
                  )}
                  <p className="mt-2 text-xs text-gray-400">
                    {formData.contenu.length} caractères
                    {isHtml ? " · HTML détecté" : " · Commencez par < pour le mode HTML"}
                  </p>
                </div>
              )}

              {activeTab === "extrait" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Extrait <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-400 font-normal ml-2">Résumé affiché dans la liste</span>
                  </label>
                  <textarea
                    required
                    rows={8}
                    value={formData.extrait}
                    onChange={(e) => setFormData({ ...formData, extrait: e.target.value })}
                    placeholder="Résumé court et accrocheur (2-3 phrases)..."
                    className="w-full bg-white border border-gray-200 rounded-xl p-5 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 focus:border-[#C9922A]/50 transition-colors"
                  />
                  <p className="mt-2 text-xs text-gray-400">{formData.extrait.length} / 300 caractères</p>
                </div>
              )}

{activeTab === "media" && (
                <div className="space-y-8">

                  {/* Image principale */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Image principale
                      <span className="text-xs text-gray-400 font-normal ml-2">Affichée en couverture de l'article</span>
                    </label>
                    <div className="flex gap-3 mb-3">
                      <input
                        type="text"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        placeholder="/images/mon-image.jpg ou https://..."
                        className="flex-1 bg-white border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors"
                      />
                      {formData.image && (
                        <button type="button" onClick={() => setFormData(f => ({ ...f, image: "" }))}
                          className="p-3 text-red-400 hover:text-red-600 border border-gray-200 rounded-xl hover:bg-red-50 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {formData.image && (
                      <img src={formData.image} alt="" className="w-full max-h-48 object-cover rounded-xl border border-gray-200 mb-3"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    )}
                  </div>

                  {/* Galerie */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-semibold text-gray-700">
                        Galerie de photos
                        <span className="text-xs text-gray-400 font-normal ml-2">{formData.galerie.length} photo{formData.galerie.length !== 1 ? "s" : ""}</span>
                      </label>
                    </div>

                    {/* Add photo to galerie */}
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={newGalerieUrl}
                        onChange={(e) => setNewGalerieUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addToGalerie(newGalerieUrl); }}}
                        placeholder="/images/photo.jpg ou https://..."
                        className="flex-1 bg-white border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors"
                      />
                      <button type="button" onClick={() => addToGalerie(newGalerieUrl)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-[#004333] text-white text-sm font-medium rounded-xl hover:bg-[#005544] transition-colors">
                        <Plus className="w-4 h-4" /> Ajouter
                      </button>
                    </div>

                    {/* Galerie grid */}
                    {formData.galerie.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                        {formData.galerie.map((url, i) => (
                          <div key={i} className="relative group rounded-xl overflow-hidden border border-gray-200">
                            <img src={url} alt="" className="w-full h-28 object-cover"
                              onError={(e) => { e.target.src = ""; e.target.style.background = "#f5f0eb"; }} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button type="button"
                                onClick={() => setFormData(f => ({ ...f, image: url }))}
                                title="Définir comme image principale"
                                className="p-1.5 bg-[#C9922A] text-white rounded-lg text-xs font-medium">
                                ★
                              </button>
                              <button type="button"
                                onClick={() => removeFromGalerie(i)}
                                className="p-1.5 bg-red-500 text-white rounded-lg">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            {formData.image === url && (
                              <div className="absolute top-1.5 left-1.5 bg-[#C9922A] text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                                Couverture
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                              {url.split("/").pop()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {formData.galerie.length === 0 && (
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm mb-4">
                        Aucune photo dans la galerie. Ajoutez des URLs ci-dessus ou sélectionnez des images locales.
                      </div>
                    )}

                    {/* Local images picker */}
                    <div>
                      {/* Upload + bibliothèque unifiée */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <Image className="w-4 h-4" /> Images locales du site
                          <span className="text-xs font-normal text-gray-400">({localImages.length})</span>
                        </p>
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004333] text-white text-xs font-medium rounded-lg hover:bg-[#005544] transition-colors disabled:opacity-50">
                          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {uploading ? "Upload..." : "Ajouter depuis mon PC"}
                        </button>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                      <p className="text-xs text-gray-400 mb-3">★ = couverture &nbsp; + = galerie &nbsp; ✕ = retirer de la liste</p>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {localImages.map(src => {
                          const isMain = formData.image === src;
                          const inGalerie = formData.galerie.includes(src);
                          return (
                            <div key={src} className="relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer"
                              style={{ borderColor: isMain ? "#C9922A" : inGalerie ? "#004333" : "transparent" }}>
                              <img src={src} alt="" className="w-full h-16 object-cover" />
                              {/* Overlay with actions */}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                <button type="button" onClick={() => setFormData(f => ({ ...f, image: src }))}
                                  title="Couverture" className="p-1 bg-[#C9922A] text-white rounded text-xs">★</button>
                                <button type="button" onClick={() => addToGalerie(src)}
                                  title="Ajouter galerie" className="p-1 bg-[#004333] text-white rounded text-xs">+</button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); removeLocalImage(src); }}
                                  title="Retirer de la bibliothèque" className="p-1 bg-red-500 text-white rounded text-xs">✕</button>
                              </div>
                              {isMain && <div className="absolute top-0.5 left-0.5 w-2 h-2 bg-[#C9922A] rounded-full" />}
                              {inGalerie && !isMain && <div className="absolute top-0.5 left-0.5 w-2 h-2 bg-[#004333] rounded-full" />}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        <span className="inline-block w-2 h-2 bg-[#C9922A] rounded-full mr-1" /> Couverture &nbsp;
                        <span className="inline-block w-2 h-2 bg-[#004333] rounded-full mr-1" /> Dans la galerie
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "seo" && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta title <span className="text-xs text-gray-400 font-normal ml-1">60 car. max</span></label>
                    <input type="text" value={formData.meta_title} onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      placeholder={formData.titre} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors" />
                    <p className="mt-1 text-xs text-gray-400">{formData.meta_title.length} / 60</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta description <span className="text-xs text-gray-400 font-normal ml-1">160 car. max</span></label>
                    <textarea rows={4} value={formData.meta_description} onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      placeholder={formData.extrait} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#C9922A]/30 transition-colors" />
                    <p className="mt-1 text-xs text-gray-400">{formData.meta_description.length} / 160</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Aperçu Google</p>
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <p className="text-blue-700 text-base font-medium truncate">{formData.meta_title || formData.titre || "Titre"}</p>
                      <p className="text-green-700 text-xs my-0.5">https://happyhouse-conciergerie.fr/blog/...</p>
                      <p className="text-gray-600 text-sm line-clamp-2">{formData.meta_description || formData.extrait || "Description..."}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesCMS;
